import getVariantInfo from "common/jiffy/get-variant";
import { track as mixpanelTrack } from "jiffy-analytics";
import { trackProductViewed as rudderTrackProductViewed } from "common/utils/rudderstack/tracker";
import { waitForWindowVariable } from "common/utils/ui/wait_for";
import selectors from "./selectors";
import {
  deliveryTimeLeftInMinutes,
  getProductProperties,
  getVariantProperties,
  getDeliveryDatesFromShipments,
  getCompositionReferrer,
  hasDirectDeliveryDateChanged
} from "./utils";

const track = (event, payload) => {
  if (window.mixpanel) {
    mixpanelTrack(event, payload);
  } else {
    waitForWindowVariable("mixpanel", () => mixpanelTrack(event, payload));
  }
};

const getIsBuyItAgainStatus = () => !!document.getElementById("js-buy-it-again-modal");
const getIsBuyItAgainDtfStatus = () => !!document.getElementById("buy-it-again-dtf-modal");
const $getActiveColor = () => document.getElementById(selectors.global.ACTIVE_COLOR_NAME);
const $getFirstVariant = color =>
  document.querySelector(
    `${selectors.global.PRODUCT_DETAILS_VARIANT_FORM} input[data-color="${color}"]`
  );
const $getTransfersVariant = () =>
  document.querySelector(selectors.global.PRODUCT_DETAILS_VARIANT_FORM);
const $getImgWithMockup = () => document.querySelector(".js-carousel-item[data-mockup-key]");
const $getDeliveryDatesContainer = () =>
  document.getElementById("js-product-details-delivery-dates-simple-container");
const getIsDtfContext = () =>
  $getDeliveryDatesContainer()?.dataset?.isDtf === "true" ||
  document.getElementById("buy-it-again-dtf-modal");

const getActiveColorWithFallback = () => {
  const $activeColorElement = $getActiveColor();
  let activeColor = $activeColorElement?.value;
  if (!activeColor) {
    const $container = $getDeliveryDatesContainer();
    const isDtfContext = getIsDtfContext();
    activeColor = isDtfContext ? "Transfer" : $container?.dataset?.selectedColor || "Transfer";
  }
  return activeColor;
};

const getPayload = async compositionDates => {
  const activeColor = getActiveColorWithFallback();
  const firstVariantId = $getFirstVariant(activeColor)?.dataset?.variantId;
  const variant = await getVariantInfo(firstVariantId);
  const compositionReferrer = getCompositionReferrer();
  const { minDeliverySeparate, maxDeliverySeparate } =
    getDeliveryDatesFromShipments(compositionDates);
  return {
    brand: variant.product.brand,
    product_category: variant.product.adwords_category,
    product: variant.product.name,
    main_product_category: variant.product.category,
    spree_composition_id: variant.compositionId,
    composition_inventory_rank: variant.compositionInventoryRank,
    composition: activeColor,
    composition_referrer: compositionReferrer,
    is_logged_in: variant.isLoggedIn,
    fulfillment_group: variant.fulfillmentGroup,
    min_delivery_separate: minDeliverySeparate,
    max_delivery_separate: maxDeliverySeparate,
    buy_it_again: getIsBuyItAgainStatus(),
    buy_it_again_dtf: getIsBuyItAgainDtfStatus(),
    has_mockup_button: !!$getImgWithMockup(),
    business_line: variant.product.business_line,
    delivery_date_considered_for: deliveryTimeLeftInMinutes(compositionDates)
  };
};

/* eslint-disable no-unused-vars */
export const sendPDPDeliveryDateRenderedTracking = ({ mixpanel }, additionalMixpanelTags = {}) => {
  // TODO: cleanup code related to delivery_dates_rendered calculations
  // Cart: https://github.com/sdtechdev/spree-jiffyshirts/pull/33466 , https://github.com/sdtechdev/spree-jiffyshirts/pull/33581
  // PDP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33379
  // PLP: https://github.com/sdtechdev/spree-jiffyshirts/pull/33623
};

export const sendPDPDeliveryDateChangeTracking = (
  previousDirectDeliveryDate,
  { composition_dates: compositionDates, mixpanel },
  additionalMixpanelTags = {}
) => {
  if (mixpanel && hasDirectDeliveryDateChanged(previousDirectDeliveryDate.text, compositionDates)) {
    const deliveryDatePayload = {
      previous_separately_shipped_delivery_date: previousDirectDeliveryDate.date,
      ...mixpanel,
      ...additionalMixpanelTags
    };
    track("pdp_delivery_date_changed", deliveryDatePayload);
  }
};

const rudderstackPayload = async () => {
  const activeColor = getActiveColorWithFallback();
  let variantDataset = $getFirstVariant(activeColor)?.dataset;
  if (!variantDataset) {
    variantDataset = $getTransfersVariant()?.dataset;
    if (!variantDataset) return {};
    return JSON.parse(variantDataset.rudderstackProductDetails);
  }
  const firstVariantId = variantDataset?.variantId;
  const imageUrl = variantDataset?.image;
  const variant = await getVariantInfo(firstVariantId);
  const { product } = variant;
  return {
    ...getProductProperties(product),
    ...getVariantProperties(variant),
    variant: activeColor,
    image_url: imageUrl,
    url: window.location.pathname,
    digital_download: false,
    pre_cut_transfers: !!document.querySelector(".js-pre-cut-checkbox")?.checked
  };
};

export const trackProductViewed = async (
  { cart_dates: cartDates, composition_dates: compositionDates, mixpanel },
  // Surfaces that emit their own canonical RudderStack Product Viewed (e.g. the
  // reorder modal) pass suppressRudderstack=true to skip this shared RudderStack
  // emit while STILL contributing the Mixpanel product_viewed event below.
  { suppressRudderstack = false } = {}
) => {
  const productViewedPayload = await getPayload(compositionDates);
  const { deliveryDate: cartDeliveryDate } = cartDates;
  const { deliveryDate: directDeliveryDate } = compositionDates;
  if (!suppressRudderstack) {
    try {
      const rudderstackEventPayload = await rudderstackPayload();
      rudderTrackProductViewed(rudderstackEventPayload);
    } catch (error) {
      console.error("Rudderstack failed");
    }
  }
  if (cartDeliveryDate || directDeliveryDate) {
    const { delivery_date_considered_for: deliveryDateConsideredFor } = mixpanel;
    track("product_viewed", {
      ...productViewedPayload,
      with_cart_delivery_date: cartDeliveryDate,
      separately_shipped_delivery_date: directDeliveryDate,
      delivery_date_considered_for: deliveryDateConsideredFor
    });
  } else {
    track("product_viewed", productViewedPayload);
  }
};

export const trackDeliveryDateApiFailed = ({ productId, compositionName, payload, error }) => {
  const errorMessage = error?.message || error?.response?.data?.error || String(error);
  const httpStatusCode = error?.response?.status || error?.status || error?.code;
  const isDtfContext = getIsDtfContext();
  const trackingPayload = {
    product_id: productId,
    composition_name: compositionName,
    error_message: errorMessage,
    http_status_code: httpStatusCode,
    page: isDtfContext ? "transfers-pdp" : "blanks-pdp",
    has_variants_hash: Boolean(payload.variants_hash),
    has_dtf_print_data: Boolean(payload.dtf_print?.length),
    is_pre_cut: Boolean(payload.pre_cut),
    design_services_enabled: !!payload.design_services_enabled,
    buy_it_again: getIsBuyItAgainStatus(),
    buy_it_again_dtf: getIsBuyItAgainDtfStatus()
  };

  track("pdp_delivery_date_api_failed", trackingPayload);
};
